import React, { useState, useEffect } from 'react';
import {
    UserPlus, Users, Search, Phone, MapPin,
    Banknote, Trash2, Edit3, X, Camera,
    FileText, LayoutGrid, List, Upload, Paperclip
} from 'lucide-react';
import api from '../api/axiosInstance';

const EmployeeManagement = () => {
    const [employees, setEmployees] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('table'); // Toggle between 'table' and 'grid'

    // Modal & Form State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '', mobile_no: '', email: '', dob: '', joining_date: '',
        address: '', assigned_site_id: '', pay_type: 'Daily', rate: '',
        bank_name: '', account_no: '', ifsc_code: '', status: 'Active'
    });

    // File States
    const [profilePic, setProfilePic] = useState(null);
    const [profilePreview, setProfilePreview] = useState(null);
    const [docFiles, setDocFiles] = useState([]);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [empRes, projRes] = await Promise.all([
                api.get('get_employees.php'),
                api.get('get_projects.php')
            ]);
            setEmployees(empRes.data.employees || []);
            setProjects(projRes.data.projects || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePic(file);
            setProfilePreview(URL.createObjectURL(file));
        }
    };

    const handleDocChange = (e) => {
        setDocFiles(Array.from(e.target.files));
    };

    const handleEdit = (emp) => {
        setEditingId(emp.id);
        setFormData({ ...emp });
        setProfilePreview(emp.profile_pic ? `https://backendibos.aartiassociates.com/uploads/profiles/${emp.profile_pic}` : null);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure? This action is permanent.")) return;
        try {
            await api.post('delete_employee.php', { id });
            loadData();
        } catch (err) { alert("Delete failed"); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const endpoint = editingId ? 'update_employee.php' : 'save_employee.php';

        const data = new FormData();
        Object.keys(formData).forEach(key => data.append(key, formData[key]));
        if (editingId) data.append('id', editingId);
        if (profilePic) data.append('profile_pic', profilePic);
        docFiles.forEach((file, i) => data.append(`doc_${i}`, file));

        try {
            await api.post(endpoint, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setIsModalOpen(false);
            resetForm();
            loadData();
        } catch (err) { alert("Save failed"); }
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({ pay_type: 'Daily', status: 'Active', name: '', mobile_no: '', email: '', dob: '', joining_date: '', address: '', assigned_site_id: '', rate: '', bank_name: '', account_no: '', ifsc_code: '' });
        setProfilePic(null);
        setProfilePreview(null);
        setDocFiles([]);
    };

    const downloadIDCard = async (empId, empName) => {
        try {
            const response = await api.get(`generate_id_card.php?id=${empId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ID_Card_${empName.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) { alert("ID Card Generation Failed"); }
    };

    const filteredEmployees = employees.filter(e => e.name.toLowerCase().includes(searchTerm.toLowerCase()));

    if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-300">Syncing Workforce...</div>;

    return (
        <div className="space-y-8 p-4 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase leading-none">Workforce Master</h1>
                    <p className="text-slate-500 font-medium italic mt-2">Employee & Labour Management Console</p>
                </div>

                <div className="flex items-center gap-4">
                    {/* View Switcher */}
                    <div className="bg-slate-100 p-1 rounded-xl flex gap-1 shadow-inner">
                        <button
                            onClick={() => setViewMode('table')}
                            className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}
                        >
                            <List size={20} />
                        </button>
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}
                        >
                            <LayoutGrid size={20} />
                        </button>
                    </div>

                    <button
                        onClick={() => { resetForm(); setIsModalOpen(true); }}
                        className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl flex items-center gap-2 hover:bg-indigo-600 transition-all"
                    >
                        <UserPlus size={18} /> Register Staff
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
                <Search className="text-slate-300" size={20} />
                <input
                    className="w-full bg-transparent outline-none font-bold text-sm uppercase"
                    placeholder="Search by name, mobile, or site..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Content View */}
            {viewMode === 'table' ? (
                <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">Employee</th>
                                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">Mobile</th>
                                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">Assigned Site</th>
                                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">Rate</th>
                                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest">KYC</th>
                                <th className="p-6 text-[10px] font-black uppercase text-slate-400 tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredEmployees.map(emp => (
                                <tr key={emp.id} className="group hover:bg-slate-50/50 transition-all">
                                    <td className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-slate-100 overflow-hidden border border-slate-200">
                                                {emp.profile_pic ? (
                                                    <img src={`https://backendibos.aartiassociates.com/uploads/profiles/${emp.profile_pic}`} className="w-full h-full object-cover" alt="" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-slate-300"><Users size={16} /></div>
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-black text-slate-900 text-sm uppercase italic leading-none">{emp.name}</p>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">ID: EMP-{emp.id.toString().padStart(4, '0')}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6 text-xs font-bold text-slate-600">{emp.mobile_no}</td>
                                    <td className="p-6">
                                        <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-[9px] font-black uppercase">
                                            {emp.assigned_site_name || 'Unassigned'}
                                        </span>
                                    </td>
                                    <td className="p-6">
                                        <p className="font-black text-slate-900 text-xs italic">₹{parseFloat(emp.rate).toLocaleString()}</p>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Per {emp.pay_type === 'Monthly' ? 'Month' : emp.pay_type === 'Hourly' ? 'Hour' : 'Day'}</p>
                                    </td>
                                    <td className="p-6">
                                        <div className="flex items-center gap-1 text-slate-400">
                                            <FileText size={14} className={emp.documents ? "text-indigo-500" : ""} />
                                            <span className="text-[10px] font-bold">{emp.documents ? JSON.parse(emp.documents).length : 0} Docs</span>
                                        </div>
                                    </td>
                                    <td className="p-6 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => downloadIDCard(emp.id, emp.name)} className="p-2 text-slate-400 hover:text-indigo-600" title="ID Card"><FileText size={18} /></button>
                                            <button onClick={() => handleEdit(emp)} className="p-2 text-slate-400 hover:text-slate-900" title="Edit"><Edit3 size={18} /></button>
                                            <button onClick={() => handleDelete(emp.id)} className="p-2 text-slate-400 hover:text-rose-500" title="Delete"><Trash2 size={18} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                /* Grid View */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredEmployees.map(emp => (
                        <div key={emp.id} className="group bg-white p-8 rounded-[48px] border border-slate-100 shadow-sm hover:shadow-2xl transition-all relative overflow-hidden">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 rounded-3xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400 border-2 border-slate-50">
                                    {emp.profile_pic ? (
                                        <img src={`https://backendibos.aartiassociates.com/uploads/profiles/${emp.profile_pic}`} className="w-full h-full object-cover" alt="" />
                                    ) : (
                                        <Users size={32} />
                                    )}
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase italic leading-none">{emp.name}</h3>
                                    <span className="text-[9px] font-black uppercase text-indigo-500 tracking-widest">{emp.pay_type} Basis</span>
                                </div>
                            </div>
                            {/* ... (Existing Card details for site, mobile, etc.) ... */}
                            <div className="flex gap-2">
                                <button onClick={() => handleEdit(emp)} className="flex-1 py-3 bg-slate-50 text-slate-900 rounded-2xl font-black text-[10px] uppercase hover:bg-indigo-600 hover:text-white transition-all">Edit</button>
                                <button onClick={() => downloadIDCard(emp.id, emp.name)} className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm"><FileText size={18} /></button>
                                <button onClick={() => handleDelete(emp.id)} className="p-3 bg-rose-50 text-rose-500 rounded-2xl hover:bg-rose-500 hover:text-white transition-all"><Trash2 size={18} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Logic Remains the Same */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleSubmit} className="bg-white w-full max-w-4xl rounded-[48px] shadow-2xl p-12 space-y-8 overflow-y-auto max-h-[90vh]">
                        {/* (Modal Form Content) */}
                        <div className="flex justify-between items-center">
                            <h2 className="text-3xl font-black italic tracking-tighter uppercase">{editingId ? 'Edit Profile' : 'New Registration'}</h2>
                            <button type="button" onClick={() => setIsModalOpen(false)} className="p-2 bg-slate-100 rounded-full"><X /></button>
                        </div>

                        {/* Profile Pic Upload */}
                        <div className="flex items-center gap-6 p-6 bg-slate-50 rounded-[32px]">
                            <div className="relative group">
                                <div className="w-24 h-24 bg-white rounded-full border-4 border-white shadow-md overflow-hidden flex items-center justify-center">
                                    {profilePreview ? (
                                        <img src={profilePreview} className="w-full h-full object-cover" alt="" />
                                    ) : (
                                        <Camera className="text-slate-300" size={32} />
                                    )}
                                </div>
                                <label className="absolute bottom-0 right-0 bg-slate-900 text-white p-2 rounded-full cursor-pointer hover:bg-indigo-600 transition-all">
                                    <Upload size={14} />
                                    <input type="file" className="hidden" onChange={handleFileChange} accept="image/*" />
                                </label>
                            </div>
                            <div>
                                <h4 className="font-black text-xs uppercase text-slate-900">Worker Profile Photo</h4>
                                <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Required for ID generation & site attendance</p>
                            </div>
                        </div>

                        {/* Input Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <h4 className="text-[10px] font-black uppercase text-indigo-500 tracking-[0.2em]">Personal Information</h4>
                                <input required className="w-full p-4 bg-slate-50 rounded-2xl font-bold" placeholder="Full Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                                <div className="grid grid-cols-2 gap-4">
                                    <input className="w-full p-4 bg-slate-50 rounded-2xl font-bold" placeholder="Mobile" value={formData.mobile_no} onChange={e => setFormData({ ...formData, mobile_no: e.target.value })} />
                                    <input type="date" className="w-full p-4 bg-slate-50 rounded-2xl font-bold" value={formData.dob} onChange={e => setFormData({ ...formData, dob: e.target.value })} />
                                </div>
                                <textarea className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm h-24" placeholder="Full Address" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} />
                            </div>

                            <div className="space-y-4">
                                <h4 className="text-[10px] font-black uppercase text-indigo-500 tracking-[0.2em]">Work & Pay Details</h4>
                                <select className="w-full p-4 bg-slate-50 rounded-2xl font-bold" value={formData.assigned_site_id} onChange={e => setFormData({ ...formData, assigned_site_id: e.target.value })}>
                                    <option value="">Assign to Project...</option>
                                    {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                                </select>
                                <div className="grid grid-cols-2 gap-4">
                                    <select className="p-4 bg-slate-50 rounded-2xl font-bold" value={formData.pay_type} onChange={e => setFormData({ ...formData, pay_type: e.target.value })}>
                                        <option value="Daily">Daily</option>
                                        <option value="Monthly">Monthly</option>
                                        <option value="Hourly">Hourly</option>
                                    </select>
                                    <input required type="number" className="p-4 bg-slate-50 rounded-2xl font-black" placeholder="Rate (₹)" value={formData.rate} onChange={e => setFormData({ ...formData, rate: e.target.value })} />
                                </div>
                                <div className="mt-4">
                                    <h4 className="text-[10px] font-black uppercase text-slate-400 mb-2">KYC Documents</h4>
                                    <label className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:border-indigo-400 transition-all">
                                        <Paperclip size={18} className="text-slate-400" />
                                        <span className="text-xs font-bold text-slate-500 uppercase">{docFiles.length > 0 ? `${docFiles.length} Files` : 'Attach Docs'}</span>
                                        <input type="file" multiple className="hidden" onChange={handleDocChange} />
                                    </label>
                                </div>
                            </div>
                        </div>

                        <button type="submit" className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl hover:bg-indigo-600 transition-all">
                            {editingId ? 'Update Record' : 'Finalize Registration'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default EmployeeManagement;